import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../../LanguageContext';
import styles from './BeforeAfter.module.css';
import before1 from "../../images/לפני אחרי פרוייקט ראשון 1.webp"
import after1 from "../../images/לפני אחרי פרוייקט ראשון 2.webp"
import before2 from "../../images/לפני אחרי פרוייקט שני 1.webp"
import after2 from "../../images/לפני אחרי פרוייקט שני 2.webp"
import before3 from "../../images/לפני אחרי פרוייקט שלישי 1.webp"
import after3 from "../../images/לפני אחרי פרוייקט שלישי 2.webp"
import before4 from "../../images/לפני אחרי פרוייקט רביעי 1.webp"
import after4 from "../../images/לפני אחרי פרוייקט רביעי 2.webp"
// Demo images - replace with your actual images
const demoProjects = [
  {
    id: 1,
    title: 'בית פרטי בשילת',
    before: before2,
    after: after2,
  },
  {
    id: 2,
    title: 'דירת יוקרה במגדל',
    before: before1,
    after: after1,
  },
  {
    id: 3,
    title: 'בית הפליסה',
    before: before3,
    after: after3,
  },
  {
    id: 4,
    title: 'פנטהאוז תל אביבי',
    before:before4,
    after: after4,
  },
];

// Single Before/After Item Component
const BeforeAfterItem = ({ project, index, beforeLabel, afterLabel }) => {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [ripples, setRipples] = useState([]);

  // Determine which label to show based on slider position (majority = over 50%)
  const showBeforeLabel = sliderPosition < 50;
  const showAfterLabel = sliderPosition >= 50;

  // Handle slider drag
  const handleSliderMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  // Mouse events for slider
  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSliderMove(e.clientX);
    addRipple(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSliderMove(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events
  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleSliderMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      handleSliderMove(e.touches[0].clientX);
    }
  };

  // Add ripple effect
  const addRipple = (clientX, clientY) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const ripple = {
      id: Date.now(),
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    setRipples(prev => [...prev, ripple]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== ripple.id));
    }, 1000);
  };

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  return (
    <article 
      className={styles.beforeAfterItem}
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      {/* Project Title */}
      <div className={styles.projectInfo}>
        <h3 className={styles.projectTitle}>{project.title}</h3>
      </div>

      {/* Comparison Container */}
      <div
        ref={containerRef}
        className={`${styles.comparisonContainer} ${isDragging ? styles.dragging : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setIsDragging(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {/* Status Label - Top Right - Shows based on majority */}
        <div className={`${styles.statusLabel} ${styles.visible}`}>
          <span>{showBeforeLabel ? beforeLabel : afterLabel}</span>
        </div>

        {/* Before Image (Background) */}
        <div className={styles.imageWrapper}>
          <img
            src={project.before}
            alt={beforeLabel}
            className={styles.beforeImage}
            draggable={false}
          loading="lazy" decoding="async" />
        </div>

        {/* After Image (Revealed by slider) */}
        <div 
          className={styles.afterWrapper}
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            src={project.after}
            alt={afterLabel}
            className={styles.afterImage}
            draggable={false}
          loading="lazy" decoding="async" />
        </div>

        {/* Slider Handle */}
        <div 
          className={styles.sliderHandle}
          style={{ left: `${sliderPosition}%` }}
        >
          <div className={styles.sliderLine}>
            <div className={styles.sliderLineGlow}></div>
          </div>
          <div className={styles.sliderKnob}>
            <div className={styles.knobInner}>
              <svg viewBox="0 0 24 24" fill="none" className={styles.knobArrows}>
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <svg viewBox="0 0 24 24" fill="none" className={styles.knobArrows}>
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.knobPulse}></div>
          </div>
        </div>

        {/* Ripple Effects */}
        {ripples.map(ripple => (
          <div
            key={ripple.id}
            className={styles.ripple}
            style={{ left: ripple.x, top: ripple.y }}
          />
        ))}

        {/* Corner Accents */}
        <div className={styles.cornerTL}></div>
        <div className={styles.cornerTR}></div>
        <div className={styles.cornerBL}></div>
        <div className={styles.cornerBR}></div>
      </div>

      {/* Progress Indicator */}
      <div className={styles.progressBar}>
        <div className={styles.progressTrack}>
          <div 
            className={styles.progressFill}
            style={{ width: `${sliderPosition}%` }}
          />
        </div>
        <div className={styles.progressLabels}>
          <span>{beforeLabel}</span>
          <span className={styles.progressPercent}>{Math.round(sliderPosition)}%</span>
          <span>{afterLabel}</span>
        </div>
      </div>
    </article>
  );
};

// Main Component
const BeforeAfter = () => {
  const { t, isEnglish } = useTranslation();
  const sectionRef = useRef(null);

  // Override project titles with translations
  const translatedProjects = demoProjects.map((p, i) => ({
    ...p,
    title: t.beforeAfter.projects[i]?.title || p.title,
  }));

  return (
    <section className={styles.beforeAfterSection} ref={sectionRef}>
      {/* Background Decorations */}
      <div className={styles.bgDecoration}>
        <div className={styles.noiseOverlay}></div>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gridLines}></div>
      </div>

      {/* Section Header */}
      <header className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t.beforeAfter.title}</h2>
        <p className={styles.sectionSubtitle}>{t.beforeAfter.subtitle}</p>
      </header>

      {/* Before/After Grid */}
      <div className={styles.itemsGrid}>
        {translatedProjects.map((project, index) => (
          <BeforeAfterItem
            key={project.id}
            project={project}
            index={index}
            beforeLabel={t.beforeAfter.before}
            afterLabel={t.beforeAfter.after}
          />
        ))}
      </div>

      {/* Bottom CTA */}
      <div className={styles.ctaWrapper}>
        <p className={styles.ctaText}>{t.beforeAfter.ctaText}</p>
        <a
          href="https://wa.me/972544998929?text=%D7%94%D7%99%D7%99%20%D7%A0%D7%A8%D7%A7%D7%99%D7%A1%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%93%D7%A3%20%D7%A9%D7%9C%D7%9A%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C.."
          target="_blank"
          rel="noopener noreferrer"
          className={styles.ctaButton}
        >
          <span>{t.beforeAfter.ctaButton}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </a>
      </div>
    </section>
  );
};

export default BeforeAfter;